import { memo, useCallback, useEffect, useState, type ChangeEvent } from 'react'
import { Navigate } from 'react-router-dom'
import { ThickArrowLeftIcon, ThickArrowRightIcon } from '@radix-ui/react-icons'
import { useTranslation } from 'react-i18next'

import { PaytrieAPI } from '@paytrie/client'
import { usePageStateStore } from '@/reducers/'
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext'
import { trackOtcRequestsListViewed } from '@/utils/analytics'
import { OtcRequestRow } from './OtcRequestRows'
import { OtcRequestCard } from './OtcRequestCard'
import {
  DEFAULT_PAGE_SIZE,
  getPageNumbers,
  PAGE_SIZE_OPTIONS,
} from '../pagination'
import Sidebar from '../Sidebar'
import Topbar from '../Topbar'
import { HistoryTabs } from '../HistoryTabs'

export const DashboardOtcRequests = memo(function DashboardOtcRequests() {
  const { t } = useTranslation()
  const { enableOtc, isLoading: isFeatureFlagsLoading } = useFeatureFlags()
  const authToken = usePageStateStore.use.authToken() || ''
  const [requests, setRequests] = useState<PaytrieAPI.OtcRequest[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [isUnavailable, setIsUnavailable] = useState(false)
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen)
  }

  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE)

  const fetchOtcRequests = useCallback(async () => {
    try {
      const response = await PaytrieAPI.listOtcRequests({
        limit: pageSize,
        offset: (currentPage - 1) * pageSize,
      })
      if (response.status === 200) {
        setRequests(response.data.data.items)
        setTotalCount(response.data.data.total)
        setIsUnavailable(false)
        trackOtcRequestsListViewed({
          page: currentPage,
          pageSize,
          count: response.data.data.items.length,
        })
      } else if (response.status === 404) {
        setRequests([])
        setTotalCount(0)
        setIsUnavailable(true)
      }
    } catch {
      console.warn('Unable to fetch OTC requests')
    }
  }, [currentPage, pageSize])

  useEffect(() => {
    if (authToken.length > 0 && enableOtc) {
      void fetchOtcRequests()
    }
  }, [authToken, enableOtc, fetchOtcRequests])

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize))
  const pageNumbers = getPageNumbers(currentPage, totalPages)

  const handlePreviousPage = () => {
    setCurrentPage((prevPage) => Math.max(prevPage - 1, 1))
  }

  const handleNextPage = () => {
    setCurrentPage((prevPage) => Math.min(prevPage + 1, totalPages))
  }

  const handlePageSizeChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setPageSize(Number(event.target.value))
    setCurrentPage(1)
  }

  if (!isFeatureFlagsLoading && !enableOtc) {
    return <Navigate to="/History" replace />
  }

  return (
    <div
      className="relative flex min-h-screen flex-col md:flex-row"
      data-testid="container-dashboard-otc-requests"
    >
      <Topbar />
      <Sidebar isOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black opacity-70 z-30"
          onClick={toggleSidebar}
          data-testid="nav-overlay"
        ></div>
      )}
      <div className="flex-grow bg-cloud dark:bg-pebble p-6 relative z-10">
        <div
          className="bg-cloud dark:bg-pebble rounded-lg flex-grow pt-16 lg:min-w-[1050px] lg:ml-[-155px] pb-20"
          data-testid="container-content-container"
        >
          <div className="flex flex-row items-center justify-between py-2">
            <h1
              className="text-3xl font-normal text-left mb-4 text-black dark:text-white"
              data-testid="container-title"
            >
              {t('dashboardHistory.title')}
            </h1>
          </div>
          <HistoryTabs active="otc" />
          {isUnavailable ? (
            <p
              className="text-gray-600 dark:text-gray-300"
              data-testid="text-otc-unavailable"
            >
              {t('dashboardOtcRequests.unavailable')}
            </p>
          ) : (
            <>
              <div
                className="flex flex-col gap-3 lg:hidden"
                data-testid="list-otc-request-cards"
              >
                {requests.map((request, index) => (
                  <OtcRequestCard
                    key={request.id}
                    index={index}
                    request={request}
                  />
                ))}
              </div>
              <div
                className="hidden overflow-x-auto lg:block"
                data-testid="text-table-container"
              >
                <table
                  className="min-w-full bg-cloud dark:bg-pebble border-collapse"
                  data-testid="item-table"
                >
                  <thead>
                    <tr data-testid="text-table-header-row">
                      <th
                        className="py-2 lg:text-xl sm:text-sm border-b-2 border-gray-300 dark:border-gray-700 text-left dark:text-gray-300"
                        data-testid="text-header-date"
                      >
                        {t('dashboardOtcRequests.table.headers.date')}
                      </th>
                      <th
                        className="py-2 lg:text-xl sm:text-sm border-b-2 border-gray-300 dark:border-gray-700 text-left dark:text-gray-300"
                        data-testid="text-header-id"
                      >
                        {t('dashboardOtcRequests.table.headers.requestId')}
                      </th>
                      <th
                        className="py-2 lg:text-xl sm:text-sm border-b-2 border-gray-300 dark:border-gray-700 text-left dark:text-gray-300"
                        data-testid="text-header-from"
                      >
                        {t('dashboardOtcRequests.table.headers.from')}
                      </th>
                      <th
                        className="py-2 lg:text-xl sm:text-sm border-b-2 border-gray-300 dark:border-gray-700 text-left dark:text-gray-300"
                        data-testid="text-header-to"
                      >
                        {t('dashboardOtcRequests.table.headers.to')}
                      </th>
                      <th
                        className="py-2 lg:text-xl sm:text-sm border-b-2 border-gray-300 dark:border-gray-700 text-center dark:text-gray-300"
                        data-testid="text-header-status"
                      >
                        {t('dashboardOtcRequests.table.headers.status')}
                      </th>
                    </tr>
                  </thead>
                  <tbody data-testid="text-table-body">
                    {requests.map((request, index) => (
                      <OtcRequestRow
                        key={request.id}
                        index={index}
                        request={request}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
        {!isUnavailable && (
          <footer
            className="w-full bg-cloud dark:bg-pebble p-4 flex flex-col items-center fixed bottom-0 left-0 z-20"
            data-testid="container-footer"
          >
            <div
              className="flex justify-center items-center space-x-4 mb-2"
              data-testid="container-controls"
            >
              <label
                className="flex items-center space-x-2 text-gray-900 dark:text-gray-100"
                data-testid="container-page-size"
              >
                <span>{t('dashboardOtcRequests.pagination.perPage')}</span>
                <select
                  value={pageSize}
                  onChange={handlePageSizeChange}
                  className="border border-gray-300 dark:border-gray-700 rounded bg-cloud dark:bg-pebble px-2 py-1"
                  data-testid="select-page-size"
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </label>
              <button
                onClick={handlePreviousPage}
                className="p-2 rounded-full disabled:opacity-50"
                disabled={currentPage === 1}
                data-testid="nav-prev-button"
              >
                <ThickArrowLeftIcon className="w-6 h-6 text-gray-900 dark:text-gray-100" />
              </button>
              <div
                className="flex items-center space-x-1"
                data-testid="container-page-numbers"
              >
                {pageNumbers.map((page, idx) =>
                  page === 'ellipsis' ? (
                    <span
                      key={`ellipsis-${idx}`}
                      className="px-2 text-gray-900 dark:text-gray-100"
                      data-testid="page-ellipsis"
                    >
                      …
                    </span>
                  ) : (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-1 rounded ${
                        page === currentPage
                          ? 'bg-black text-white dark:bg-white dark:text-black'
                          : 'text-gray-900 dark:text-gray-100'
                      }`}
                      aria-current={page === currentPage ? 'page' : undefined}
                      data-testid={`nav-page-${page}`}
                    >
                      {page}
                    </button>
                  )
                )}
              </div>
              <button
                onClick={handleNextPage}
                className="p-2 rounded-full disabled:opacity-50"
                disabled={currentPage === totalPages}
                data-testid="nav-next-button"
              >
                <ThickArrowRightIcon className="w-6 h-6 text-gray-900 dark:text-gray-100" />
              </button>
            </div>
          </footer>
        )}
      </div>
    </div>
  )
})
